<template>
<div>
    <div class="flex jc-sb mb-3">
        <div class="mr-2">
            <button
                title="Filters"
                type="button"
                :disabled="is_loading_users"
                :class="{ 'primary': show_filters }"
                @click="show_filters = !show_filters">
                <i class="material-icons">filter_list</i>
                <span v-if="show_filters">Hide Filters</span>
                <span v-else>Show Filters</span>
            </button>
        </div>

        <div>
            <button class="mr-3" type="button" :disabled="is_loading_users" @click="onRefreshUsers">
                <i class="material-icons">refresh</i>
            </button>

            <button v-if="store.getters.hasPermissions(['admin'])" class="primary" type="button" @click="show_new_user_modal = true">Add User</button>
        </div>
    </div>

    <form v-show="show_filters" class="flex ai-c mt-3" @submit.prevent="onFilterSubmit">
        <div class="flex-a flex ai-c mr-2 fs-09">
            <div class="mr-2">
                <label>Username</label>
                <input type="text" v-model="filters.username" />
            </div>
        </div>

        <div class="ml-2">
            <button type="reset" @click="onResetFilters">Reset</button>
            <button type="submit">Search</button>
        </div>
    </form>

    <div class="mt-3">
        <div class="mt-3 hint">Click a user to view their facility information.</div>

        <div class="o-a mb-3">
            <vue-table-pagination
                ref="users_table_pagination"
                @vuetable-pagination:change-page="onChangePage">
            </vue-table-pagination>
        </div>

        <div :class="['table-wrapper', 'table-loads', { loading: is_loading_users }]">
            <div class="table-error" v-show="load_users_error">
                <span>{{ load_users_error }}</span>
                <button class="text mt-2" :disabled="is_loading_users" @click="onRefreshUsers">Retry?</button>
            </div>

            <vue-table ref="users_table"
                api-url="/api/users"
                :append-params="api_params"
                data-path="data.data"
                :per-page="100"
                table-height="475px"
                pagination-path="data.pagination"
                :http-options="http_options"
                :fields="columns"
                detail-row-component="users-table-detail-row"
                @vuetable:row-clicked="onRowClicked"
                @vuetable:pagination-data="onPaginationData"
                @vuetable:loading="is_loading_users = true"
                @vuetable:loaded="is_loading_users = false">
                <template slot="username" slot-scope="props">
                    <div class="flex ai-c jc-sb">
                        <span>{{ props.rowData.username }}</span>

                        <span v-if="props.rowData.is_admin" class="badge fs-09">
                            Admin
                        </span>
                        <span v-else-if="props.rowData.is_monitor" class="badge warning fs-09">
                            Monitor
                        </span>
                    </div>
                </template>

                <template slot="activity" slot-scope="props">
                    <span
                        v-if="props.rowData.last_active_date === null"
                        title="Never active"
                        class="gray-message">--</span>
                    <span
                        v-else-if="props.rowData.days_since >= 3"
                        :title="`Active ${props.rowData.days_since} days ago`"
                        class="activity-dot rogue" />
                    <span
                        v-else-if="props.rowData.hours_since >= 1"
                        :title="`Active ${props.rowData.hours_since} hours ago`"
                        class="activity-dot offline" />
                    <span
                        v-else-if="props.rowData.minutes_since >= 45"
                        :title="`Active ${props.rowData.minutes_since} minutes ago`"
                        class="activity-dot offline" />
                    <span
                        v-else-if="props.rowData.minutes_since >= 15"
                        :title="`Active ${props.rowData.minutes_since} minutes ago`"
                        class="activity-dot recent" />
                    <span
                        v-else-if="props.rowData.minutes_since >= 1"
                        :title="`Active ${props.rowData.minutes_since} minutes ago`"
                        class="activity-dot active" />
                    <span
                        v-else
                        :title="`Active ${props.rowData.seconds_since} seconds ago`"
                        class="activity-dot active" />
                </template>

                <template slot="facility" slot-scope="props">
                    <span class="success-text" v-if="props.rowData.facility !== null">
                        <i class="material-icons">check_circle</i>
                    </span>
                </template>

                <template slot="actions" slot-scope="props">
                    <button
                        class="tertiary icon"
                        type="button"
                        @click.stop="onAskDeleteUser(props.rowData)">
                        <i class="material-icons">delete</i>
                    </button>

                    <button
                        class="ml-3 icon"
                        type="button"
                        @click.stop="onEditUser(props.rowData)">
                        <i class="material-icons">edit</i>
                    </button>
                </template>
            </vue-table>
        </div>
    </div>

    <modal :no-footer="true" size="medium" v-show="show_new_user_modal" @close="show_new_user_modal = false">
        <template slot="header">
            New User
        </template>

        <template slot="content">
            <form @submit.prevent="onCreateUser">
                <div class="flex">
                    <div class="flex-50 mr-3">
                        <label for="new-user-username">Username</label>
                        <input id="new-user-username" type="text" v-model="new_user.username" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="new-user-password">Password</label>
                        <password-input
                            id="new-user-password"
                            required="required"
                            :input-value="new_user.password"
                            @change="(p) => new_user.password = p"></password-input>
                    </div>
                </div>

                <div class="flex mt-3">
                    <div class="flex-50 mr-3">
                        <label for="new-user-email">Email</label>
                        <input id="new-user-email" required="required" type="email" v-model="new_user.email" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="new-user-real-name">Real Name (optional)</label>
                        <input id="new-user-real-name" type="text" v-model="new_user.real_name" />
                    </div>
                </div>

                <hr />

                <label>Available Permissions</label>

                <div class="flex mt-2">
                    <div class="">
                        <checkbox
                            :checked="new_user.is_admin"
                            @click="new_user.is_admin = !new_user.is_admin">
                            Administrator</checkbox>
                    </div>

                    <div class="ml-4">
                        <checkbox
                            :checked="new_user.is_monitor"
                            @click="new_user.is_monitor = !new_user.is_monitor">
                            Monitor</checkbox>
                    </div>
                </div>

                <div class="flex ai-c jc-sb mb-2 mt-3">
                    <div class="mr-3">
                        {{ save_user_error }}
                    </div>

                    <div class="flex ml-3">
                        <div v-show="is_saving_user" class="flex ai-c mr-2">
                            <img src="../../assets/img/loading-black-32.gif" alt="loading" />
                        </div>

                        <button :disabled="is_saving_user" type="submit">Create</button>
                    </div>
                </div>
            </form>
        </template>
    </modal>

    <modal :no-footer="true" size="large" v-show="show_edit_user_modal" @close="show_edit_user_modal = false; edit_user = {}">
        <template slot="header">
            {{ edit_user.username }} ({{ edit_user.email }})
        </template>

        <template slot="content">
            <form @submit.prevent="onSaveUser">
                <div class="flex">
                    <div class="flex-50 mr-3">
                        <label for="edit-user-username">Username</label>
                        <input id="edit-user-username" type="text" v-model="edit_user.username" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="edit-user-password">Password</label>
                        <password-input
                            id="edit-user-password"
                            label="edit-user-password"
                            :input-value="edit_user.password"
                            @change="(p) => edit_user.password = p"></password-input>
                    </div>
                </div>

                <div class="flex mt-3">
                    <div class="flex-50 mr-3">
                        <label for="edit-user-email">Email</label>
                        <input id="edit-user-email" required="required" type="email" v-model="edit_user.email" />
                    </div>

                    <div class="flex-50 ml-3">
                        <label for="edit-user-real-name">Real Name (optional)</label>
                        <input id="edit-user-real-name" type="text" v-model="edit_user.real_name" />
                    </div>
                </div>

                <hr />

                <label>Available Permissions</label>

                <div class="flex mt-2">
                    <div class="">
                        <checkbox
                            :checked="edit_user.is_admin"
                            @click="edit_user.is_admin = !edit_user.is_admin">
                            Administrator</checkbox>
                    </div>

                    <div class="ml-4">
                        <checkbox
                            :checked="edit_user.is_monitor"
                            @click="edit_user.is_monitor = !edit_user.is_monitor">
                            Monitor</checkbox>
                    </div>
                </div>

                <div class="flex ai-c jc-sb mb-2 mt-3">
                    <div class="mr-3">
                        {{ save_user_error }}
                    </div>

                    <div class="flex ml-3">
                        <div v-show="is_saving_user" class="flex ai-c mr-2">
                            <img src="../../assets/img/loading-black-32.gif" alt="loading" />
                        </div>

                        <button :disabled="is_saving_user" type="submit">Update</button>
                    </div>
                </div>
            </form>
        </template>
    </modal>
</div>
</template>

<script>
import http from "@/http"
import store from "@/store"
import Vue from "vue"
import { objectToParams } from "@/utilities/form-data"

export default {
    data () {
        return {
            columns: [
                { name: "__slot:username", title: "Username" },
                { name: "__slot:activity", title: "Activity", width: "125px", dataClass: "center aligned", titleClass: "center aligned" },
                { name: "real_name", title: "Real Name" },
                { name: "email", title: "Email" },
                { name: "__slot:facility", title: "Owns Facility", width: "150px", dataClass: "center aligned", titleClass: "center aligned" },
                { name: "__slot:actions", dataClass: "center aligned" }
            ],
            filters: {
                username: ""
            },
            http_options: {
                baseURL: this.$http.defaults.baseURL,
                headers: {
                    "X-Authorization": store.state.token.token
                },
                transformResponse: [(raw) => {
                    try {
                        let data = JSON.parse(raw)

                        this.load_users_error = (data.error) ? data.data : ""

                        return data
                    } catch (error) {
                        this.load_users_error = "Unknown error loading users."

                        return raw
                    }
                }]
            },
            load_users_error: "",
            save_user_error: "",
            is_loading_users: false,
            is_saving_user: false,
            edit_user: {
                id: null,
                email: "",
                is_admin: false,
                is_monitor: false,
                password: "",
                real_name: "",
                username: ""
            },
            new_user: {
                email: "",
                is_admin: false,
                is_monitor: false,
                password: "",
                real_name: "",
                username: ""
            },
            show_edit_user_modal: false,
            show_filters: false,
            show_new_user_modal: false,
            store: store
        }
    },

    computed: {
        api_params () {
            const params = {}

            for (const key in this.filters) {
                const value = this.filters[key]

                if (value.length > 0) {
                    params[key] = value
                }
            }

            return params
        }
    },

    methods: {
        /**
         * Show a confirmation dialog box before deleting the user.
         *
         * @param {object} user User data object
         */
        async onAskDeleteUser (user) {
            const confirmation = confirm(`Are you sure you wish to delete '${user.username}'?`)

            if (confirmation) {
                this.is_loading_users = true

                try {
                    await http.delete(`/api/users/${user.id}`)

                    this.$eventManager.emit("add-toast", { message: `User ${user.username} has been deleted!` })
                    this.$refs.users_table.reload()
                } catch (ex) {
                    this.$modalManager.add({ state: "bad", header: "Error", body: ex })
                } finally {
                    this.is_loading_users = false
                }
            }
        },

        /**
         * Toggle the detail row for the clicked record if it has a facility associated with it.
         *
         * @param object data   Row data
         * @param object field Field column configuration
         * @param Event  ev    Event object
         *
         * @return null
         */
        onRowClicked (data, field, ev) {
            if (data.facility !== null) {
                this.$refs.users_table.toggleDetailRow(data.id)
            }
        },

        /**
         * Update the pagination component when data is received.
         *
         * @param object paginationData Pagination data
         *
         * @return null
         */
        onPaginationData (paginationData) {
            this.$refs.users_table_pagination.setPaginationData(paginationData)
        },

        /**
         * Change the pagination page when requested.
         *
         * @param object page Page
         *
         * @return null
         */
        onChangePage (page) {
            this.$refs.users_table.changePage(page)
        },

        /**
         * Send the request to create the new user. Then refresh the users table.
         *
         * @return null
         */
        async onCreateUser () {
            const params = objectToParams(this.new_user, { dontConvertDate: true })

            this.is_saving_user = true
            this.save_user_error = ""

            try {
                const { data: response } = await http.post("/api/users", params)

                this.$eventManager.emit("add-toast", { message: `User ${response.username} has been created!` })

                this.show_new_user_modal = false
                this.$refs.users_table.reload()

                // reset the new user data
                Object.keys(this.new_user).forEach(key => {
                    switch (typeof this.new_user[key]) {
                    case "boolean":
                        this.new_user[key] = false
                        break
                    case "number":
                        this.new_user[key] = 0
                        break
                    case "string":
                    default:
                        this.new_user[key] = ""
                    }
                })
            } catch (ex) {
                this.save_user_error = ex
            } finally {
                this.is_saving_user = false
            }
        },

        /**
         * Copy the user data to the edit user data so it can be edited.
         *
         * @param object user User data
         *
         * @return null
         */
        onEditUser (user) {
            // don't replace the object, as it will break reactivity
            for (const key in user) {
                if (user.hasOwnProperty(key)) {
                    this.edit_user[key] = user[key]
                }
            }

            this.show_edit_user_modal = true
        },

        onResetFilters () {
            for (const key in this.filters) {
                this.filters[key] = ""
            }

            Vue.nextTick(() => {
                this.$refs.users_table.reload()
            })
        },

        onFilterSubmit () {
            Vue.nextTick(() => {
                this.$refs.users_table.reload()
            })
        },

        /**
         * Refresh the users table.
         *
         * @return null
         */
        onRefreshUsers () {
            this.$refs.users_table.reload()
        },

        /**
         * Send the request to the server to save the updated user.
         *
         * @return null
         */
        async onSaveUser () {
            const params = objectToParams(this.edit_user, { dontConvertDate: true })

            this.is_saving_user = true

            try {
                await http.post(`/api/users/${this.edit_user.id}`, params)

                this.show_edit_user_modal = false
                this.save_user_error = ""

                this.$refs.users_table.reload()

                // reset the edit user data
                this.edit_user = {}
            } catch (ex) {
                this.save_user_error = ex
            } finally {
                this.is_saving_user = false
            }
        }
    }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/components/manage-users-page/UserAccountsTab.vue